import React, { useState, useRef, useEffect } from "react";
import { Send, Smile, ChevronUp, ChevronDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";

// Couleurs du drapeau du Niger
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

// Composant emoji-picker simplifié
const SimpleEmojiPicker = ({ onEmojiClick }) => {
  const commonEmojis = [
    "😀",
    "😃",
    "😄",
    "😁",
    "😆",
    "😅",
    "😂",
    "🤣",
    "😊",
    "😇",
    "🙂",
    "🙃",
    "😉",
    "😌",
    "😍",
    "🥰",
    "👍",
    "👏",
    "🙌",
    "🤝",
    "❤️",
    "✅",
    "👌",
    "🎉",
  ];

  return (
    <div className="p-4 bg-white rounded-b-lg border-t">
      <div className="grid grid-cols-8 gap-2">
        {commonEmojis.map((emoji, index) => (
          <button
            key={index}
            type="button" // Définir le type comme button pour éviter la soumission du formulaire
            className="p-1 hover:bg-gray-100 rounded text-lg"
            onClick={(e) => {
              e.preventDefault(); // Empêcher la propagation de l'événement
              onEmojiClick(emoji);
            }}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
};

const ModernMessageInput = ({ message, handleMessageChange, envoyer }) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const textAreaRef = useRef(null);
  const emojiPickerRef = useRef(null);

  // Gérer la taille automatique du textarea
  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = "auto";
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
  }, [message]);

  // Focus sur le textarea quand on change l'état d'expansion
  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [isExpanded]);

  // Fermer le sélecteur d'émojis lorsqu'on clique ailleurs
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        !event.target.closest('button[data-emoji-toggle="true"]')
      ) {
        setShowEmojiPicker(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleEmojiClick = (emoji) => {
    const cursorPosition =
      textAreaRef.current?.selectionStart || message.length;
    const newMessage =
      message.substring(0, cursorPosition) +
      emoji +
      message.substring(cursorPosition);
    handleMessageChange(newMessage);

    // Option: garder le focus sur le textarea après avoir inséré un emoji
    setTimeout(() => {
      if (textAreaRef.current) {
        textAreaRef.current.focus();
        const newPosition = cursorPosition + emoji.length;
        textAreaRef.current.setSelectionRange(newPosition, newPosition);
      }
    }, 10);
  };

  const toggleEmojiPicker = (e) => {
    e.preventDefault(); // Empêcher la soumission du formulaire
    setShowEmojiPicker(!showEmojiPicker);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (message.trim().length) {
          envoyer(e);
          if (textAreaRef.current) {
            textAreaRef.current.style.height = "auto";
          }
        }
      }}
      className="p-3 border-t bg-white"
    >
      <div className="relative">
        <div
          className={`flex flex-col transition-all duration-300 ${
            isExpanded ? "h-48" : "h-auto"
          }`}
        >
          <div
            className={`relative border rounded-2xl overflow-hidden shadow-sm transition-all duration-300 
                    ${isExpanded ? "h-40" : "min-h-[56px] max-h-32"}`}
            style={{
              borderColor: message.length ? COLORS.green : COLORS.lightGray,
              backgroundColor: COLORS.white,
            }}
          >
            <textarea
              ref={textAreaRef}
              value={message}
              onChange={(e) => handleMessageChange(e.target.value)}
              placeholder="Écrivez votre message..."
              className={`w-full px-4 py-3 resize-none outline-none ${
                isExpanded ? "h-full" : "max-h-28"
              }`}
              style={{
                fontSize: "16px",
                overflow: "auto",
              }}
              rows={isExpanded ? 5 : 1}
            />

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute top-0 right-0 p-1 m-1 opacity-50 hover:opacity-100"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </Button>
          </div>

          {/* Barre d'outils moderne */}
          <div className="flex justify-between items-center mt-2">
            <div className="flex space-x-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      data-emoji-toggle="true"
                      variant="ghost"
                      size="icon"
                      type="button"
                      className="rounded-full w-10 h-10 hover:bg-green-50"
                      onClick={toggleEmojiPicker}
                      style={{ color: COLORS.green }}
                    >
                      <Smile className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Ajouter un emoji</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <button
              type="submit"
              className={`rounded-full flex items-center justify-center w-12 h-12 transition-all duration-300 ${
                message.trim().length
                  ? "scale-100 bg-[#E87E04]"
                  : "scale-90 opacity-70 bg-[#E2E8F0]"
              }`}
              disabled={!message.trim().length}
            >
              <Send className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Sélecteur d'emoji avec animation */}
        {showEmojiPicker && (
          <div
            ref={emojiPickerRef}
            className="absolute bottom-16 right-0 z-10 shadow-lg rounded-lg border animate-fade-in-up"
          >
            <div className="p-2 bg-white rounded-t-lg border-b flex justify-between items-center">
              <span className="text-sm font-medium">Emojis</span>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                className="h-8 w-8 p-1 rounded-full"
                onClick={(e) => {
                  e.preventDefault();
                  setShowEmojiPicker(false);
                }}
              >
                <X size={16} />
              </Button>
            </div>
            <SimpleEmojiPicker onEmojiClick={handleEmojiClick} />
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.2s ease-out;
        }
      `}</style>
    </form>
  );
};

export default ModernMessageInput;
